/**
 * Environment configuration
 *
 * Multi-tenancy URL resolution:
 *
 * The API serves this app (wwwroot/react), so the API is always the page's own origin:
 * hgv.mot-r.app calls hgv.mot-r.app. Nothing here names a domain — the backend's
 * App:SelfUrl is the one place the address is configured.
 *
 * Development: a page on the Vite dev server's port calls the same hostname on the API port,
 * e.g. blackrock.motrv2.com:44200 calls blackrock.motrv2.com:44368. Pages the API serves
 * through its dev proxy (SpaHosting:DevProxyEnabled) — on 44368 or a tunnel host such as
 * hgv-motr.initto.work — are same-origin.
 *
 * VITE_API_URL still overrides everything (local tunnel testing).
 */

/**
 * Get the API URL from the current page's address, so each tenant subdomain calls its own
 * API host.
 */
const getApiUrl = (): string => {
  // Full URL override (backwards compatibility)
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // Development, page opened on the Vite dev server's own port: the API is on 44368.
  // A page on 44368 or on a tunnel host (no port) is served through the API, so it is same-origin.
  const { port } = window.location;
  if (import.meta.env.DEV && port !== '' && port !== '44368') {
    return `https://${window.location.hostname}:44368`;
  }

  // Served by the API itself: same origin.
  return window.location.origin;
};

export const config = {
  appName: import.meta.env.VITE_APP_NAME || 'motr',
  apiUrl: getApiUrl(),
  buildTime: import.meta.env.VITE_BUILD_TIME || new Date().toISOString(),
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
  dateMinYear: parseInt(import.meta.env.VITE_DATE_MIN_YEAR || '1990', 10),
  dateMaxYearOffset: parseInt(import.meta.env.VITE_DATE_MAX_YEAR_OFFSET || '1', 10),
} as const;

export const apiConfig = {
  baseUrl: config.apiUrl,
  timeout: 60000, // 60 seconds to handle cache rebuilds
  endpoints: {
    // Authentication
    token: '/connect/token',
    applicationConfiguration: '/api/abp/application-configuration',

    // Jobs
    jobs: '/api/app/jobs-ext',
    getJobs: '/api/app/jobs-ext/get-jobs',
    jobStepDetail: '/api/app/jobs-ext/get-job-step-detail',

    // Dashboard
    dashboardWip: '/api/app/dashboard/wip',
    dashboardWorkload: '/api/app/dashboard/workload',
    dashboardSummary: '/api/app/dashboard/summary',

    // Reports
    performanceDetails: '/api/app/report-ext/performance-details',
    pipelineReport: '/api/app/report-ext/pipeline-report',
    durationReport: '/api/app/report-ext/duration-report',

    // Messages
    messages: '/api/app/message-extn',
    messagesPagedList: '/api/app/message-extn/get-paged-list',

    // AI
    aiSendMessage: '/api/app/ai-ext/send-message-to-ai',
    aiProviders: '/api/app/ai-provider-ext',
    aiModels: '/api/app/ai-model-ext',

    // Settings
    settings: '/api/app/setting-ext/get-settings-by-names',
    setSettings: '/api/app/setting-ext/set-settings',
  },
} as const;

export const signalRConfig = {
  hubUrl: `${config.apiUrl}/userCommunicaitonHub`,
  reconnectDelays: [0, 2000, 5000, 10000, 30000],
} as const;
